import React, { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import CallbackForm from "./CallbackForm";
import { setActiveTab } from "../../../../store/activeTabSlice";
import {
  TONES,
  CALLBACK_BAND,
  PRICE_PROMISE,
} from "../../../../constants/tradeVerticals";

/**
 * Renders one Trade vertical page (Information / Finance / Risk) from a config
 * object in constants/tradeVerticals.js. Every "Talk to our team" / product CTA
 * scrolls the user down to the callback form.
 */
const TradeVerticalPage = ({ config }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const isMember = !!userInfo?.is_member;

  // Drive the top-bar title + sidebar active state (keys off the nav item name).
  useEffect(() => {
    dispatch(setActiveTab(config.eyebrow));
  }, [dispatch, config.eyebrow]);

  const tone = TONES[config.accent] || TONES.blue;

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Card CTA routing: "search" → company search engine, "membership" → plans,
  // everything else drops the user at the callback form.
  const handleCardCta = (card) => {
    if (card.ctaAction === "search") navigate("/search");
    else if (card.ctaAction === "membership") navigate("/plans");
    else scrollToForm();
  };

  const ctaStyles = {
    buy: { background: "#FB512D", color: "#fff", border: "none" },
    talk: { background: "#fff", color: "#FB512D", border: "1.5px solid #FB512D" },
    member: { background: "#fff", color: "#9A6B12", border: "1.5px solid #E3CB86" },
  };
  const ctaBase = {
    borderRadius: "9px",
    padding: "10px 18px",
    fontSize: "13.5px",
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
  };

  const eyebrowStyle = {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: tone.eyebrow,
    marginBottom: "10px",
  };

  const cardTagStyle = (cardTone) => {
    const t = TONES[cardTone || config.accent] || tone;
    return {
      display: "inline-block",
      background: t.bg,
      color: t.text,
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: ".5px",
      textTransform: "uppercase",
      padding: "4px 9px",
      borderRadius: "6px",
      whiteSpace: "nowrap",
    };
  };

  return (
    <div style={{ maxWidth: "1120px", margin: "0 auto", textAlign: "left", color: "#002940", fontFamily: "'DM Sans', sans-serif" }}>
      {/* ===== Hero ===== */}
      <div style={{ paddingBottom: "28px", borderBottom: "1px solid #DDE4E9" }}>
        <div style={eyebrowStyle}>{config.eyebrow}</div>
        <h1
          style={{
            margin: 0,
            fontSize: "34px",
            fontWeight: 700,
            color: "#002940",
            letterSpacing: "-.6px",
            maxWidth: "680px",
            lineHeight: 1.12,
          }}
        >
          {config.headline}
        </h1>
        <p
          style={{
            margin: "14px 0 0",
            fontSize: "15.5px",
            color: "#5A6B73",
            maxWidth: "660px",
            lineHeight: 1.6,
          }}
        >
          {config.sub}
        </p>
        <div style={{ marginTop: "22px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={scrollToForm}
            style={{
              background: "#FB512D",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              padding: "13px 24px",
              fontSize: "14.5px",
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Talk to our team
          </button>
          <span style={{ fontSize: "12.5px", color: "#9AA8AF", maxWidth: "330px", lineHeight: 1.45 }}>
            Tell us what you need. A member of the TREVEX team calls you back, usually the same working day.
          </span>
        </div>
      </div>

      {/* ===== Product groups ===== */}
      {config.groups.map((group, gi) => (
        <div key={gi} style={{ marginTop: "38px" }}>
          <div style={eyebrowStyle}>{group.kicker}</div>
          <h2 style={{ margin: "0 0 4px", fontSize: "21px", fontWeight: 700, color: "#002940", letterSpacing: "-.2px" }}>
            {group.title}
          </h2>
          {group.intro && (
            <p style={{ margin: "8px 0 0", fontSize: "14px", color: "#7C8B92", maxWidth: "640px", lineHeight: 1.55 }}>
              {group.intro}
            </p>
          )}

          <div
            style={{
              display: "grid",
              marginTop: "18px",
              gap: "18px",
              gridTemplateColumns: group.cards.some((c) => c.feature)
                ? "repeat(2, minmax(0, 1fr))"
                : group.cards.length === 1
                ? "minmax(0, 560px)"
                : group.cards.length >= 3
                ? "repeat(3, minmax(0, 1fr))"
                : "repeat(2, minmax(0, 1fr))",
            }}
          >
            {group.cards.map((card, ci) => {
              const feature = !!card.feature;
              return (
                <div
                  key={ci}
                  style={{
                    background: feature ? "#FFFDF8" : "#fff",
                    border: `1px solid ${feature ? "#E7D7AE" : "#E7ECEF"}`,
                    borderRadius: "14px",
                    padding: "22px",
                    boxShadow: "0 1px 2px rgba(0,0,0,.04)",
                    display: "flex",
                    flexDirection: feature ? "row" : "column",
                    flexWrap: feature ? "wrap" : "nowrap",
                    alignItems: feature ? "center" : "stretch",
                    gap: feature ? "30px" : 0,
                    gridColumn: feature ? "1 / -1" : "auto",
                  }}
                >
                  <div style={{ flex: feature ? "1 1 360px" : 1, maxWidth: feature ? "660px" : "none" }}>
                    <div style={{ marginBottom: "14px" }}>
                      <span style={cardTagStyle(card.tone)}>{card.tag}</span>
                    </div>
                    <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: 700, color: "#002940" }}>
                      {card.title}
                    </h3>
                    <div style={{ fontSize: "20px", fontWeight: 700, color: "#002940", letterSpacing: "-.3px" }}>
                      {card.price}
                    </div>
                    {card.memberRate && (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          marginTop: "8px",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#9A6B12",
                          background: "#F6ECD2",
                          padding: "4px 10px",
                          borderRadius: "20px",
                        }}
                      >
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#C8912F" }} />
                        {card.memberRate}
                      </div>
                    )}
                    <p style={{ margin: "14px 0 0", fontSize: "13.5px", color: "#5A6B73", lineHeight: 1.55 }}>
                      {card.body}
                    </p>
                  </div>
                  <div
                    style={
                      feature
                        ? { flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "9px" }
                        : { display: "flex", alignItems: "center", gap: "13px", flexWrap: "wrap", marginTop: "18px" }
                    }
                  >
                    <button
                      type="button"
                      onClick={() => handleCardCta(card)}
                      style={{ ...ctaBase, ...(ctaStyles[card.ctaKind] || ctaStyles.talk) }}
                    >
                      {card.cta}
                    </button>
                    {card.micro && <span style={{ fontSize: "12px", color: "#9AA8AF" }}>{card.micro}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* ===== Explanatory note ===== */}
      {config.note && (
        <div
          style={{
            marginTop: "34px",
            background: "#fff",
            border: "1px solid #E7ECEF",
            borderLeft: "3px solid #FB512D",
            borderRadius: "12px",
            padding: "22px 24px",
          }}
        >
          <h3 style={{ margin: "0 0 7px", fontSize: "15.5px", fontWeight: 700, color: "#002940" }}>
            {config.note.title}
          </h3>
          <p style={{ margin: 0, fontSize: "13.5px", color: "#5A6B73", lineHeight: 1.6, maxWidth: "780px" }}>
            {config.note.body}
          </p>
        </div>
      )}

      {/* ===== Non-member upsell panel ===== */}
      {config.nonMember && !isMember && (
        <div
          style={{
            marginTop: "34px",
            background: "#002940",
            borderRadius: "14px",
            padding: "26px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "22px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: "240px" }}>
            <h3 style={{ margin: "0 0 7px", fontSize: "18px", fontWeight: 700, color: "#fff" }}>
              {config.nonMember.heading}
            </h3>
            <p style={{ margin: 0, fontSize: "13.5px", color: "#AEC0CB", lineHeight: 1.6, maxWidth: "620px" }}>
              {config.nonMember.body}
            </p>
          </div>
          {/* NOTE: "Become a Member" CTA intentionally omitted until the TIP-489
              membership page exists (membership purchase is company-gated). */}
        </div>
      )}

      {/* ===== Callback band (the form) ===== */}
      <div
        ref={formRef}
        style={{
          marginTop: "34px",
          background: "#EEF3F6",
          border: "1px solid #E0E8ED",
          borderRadius: "16px",
          padding: "28px",
          scrollMarginTop: "80px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "32px",
            alignItems: "start",
          }}
        >
          <div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: 700, color: "#002940" }}>
              {CALLBACK_BAND.heading}
            </h3>
            <p style={{ margin: "0 0 14px", fontSize: "14px", color: "#5A6B73", lineHeight: 1.6 }}>
              {CALLBACK_BAND.body}
            </p>
            <div style={{ display: "flex", gap: "9px", alignItems: "flex-start" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1B7A45" strokeWidth="2" style={{ flexShrink: 0, marginTop: "2px" }}>
                <path d="M5 12.5 10 17.5 19 7" />
              </svg>
              <span style={{ fontSize: "12.5px", color: "#7C8B92", lineHeight: 1.5 }}>
                {CALLBACK_BAND.reassurance}
              </span>
            </div>
          </div>

          <CallbackForm
            product={config.product}
            options={config.callbackOptions}
            isMember={isMember}
          />
        </div>
      </div>

      {/* ===== Price-promise footer ===== */}
      <div style={{ marginTop: "26px", display: "flex", gap: "11px", alignItems: "flex-start", padding: "4px 2px" }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#9AA8AF" strokeWidth="1.9" style={{ flexShrink: 0, marginTop: "1px" }}>
          <rect x="4" y="10" width="16" height="10" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
        <p style={{ margin: 0, fontSize: "12.5px", color: "#9AA8AF", lineHeight: 1.6, maxWidth: "820px" }}>
          {PRICE_PROMISE}
        </p>
      </div>
    </div>
  );
};

export default TradeVerticalPage;
